import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { shiftMarketplaceApi } from '../services/api';
import { ShiftMarketplaceRequest } from '../types';
import { useAuth } from '../context/AuthContext';

const parseLocalDateTime = (dateTimeStr: string): Date => {
  if (!dateTimeStr) return new Date();
  try {
    if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
};

const formatShiftRange = (start?: string, end?: string) => {
  if (!start || !end) return '—';
  const s = parseLocalDateTime(start);
  const e = parseLocalDateTime(end);
  return `${format(s, 'EEE d MMM HH:mm')} – ${format(e, 'HH:mm')}`;
};

const statusClass = (status: string) => {
  switch (status) {
    case 'Open':
      return 'bg-blue-100 text-blue-800';
    case 'PendingApproval':
      return 'bg-amber-100 text-amber-900';
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Rejected':
    case 'Cancelled':
    case 'Expired':
      return 'bg-neutral-200 text-neutral-700';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
};

const ShiftMarketplace: React.FC = () => {
  const { employee } = useAuth();
  const [tab, setTab] = useState<'open' | 'mine'>('open');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ShiftMarketplaceRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await shiftMarketplaceApi.getRequests(
        tab === 'mine' ? { mine: true } : { status: 'Open' }
      );
      setRows(data);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (e?.response?.status === 403
            ? 'You do not have access to marketplace proposals.'
            : 'Failed to load marketplace')
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const myId = employee?.id;

  const visible = useMemo(() => {
    if (tab === 'open') return rows.filter((r) => r.status === 'Open');
    return rows;
  }, [rows, tab]);

  const runAction = async (id: number, action: () => Promise<unknown>) => {
    try {
      setBusyId(id);
      setError(null);
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Marketplace Proposals</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Browse open cover offers, accept or decline swap proposals, and track your own requests.
            Manager approval is required before the roster updates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/my-schedules"
            className="px-3 py-2 rounded-lg border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            My Schedules
          </Link>
          <button
            type="button"
            onClick={load}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-neutral-200">
        <button
          type="button"
          onClick={() => setTab('open')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'open' ? 'border-blue-600 text-blue-700' : 'border-transparent text-neutral-500'
          }`}
        >
          Open shifts
        </button>
        <button
          type="button"
          onClick={() => setTab('mine')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'mine' ? 'border-blue-600 text-blue-700' : 'border-transparent text-neutral-500'
          }`}
        >
          My requests
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="text-neutral-500 py-12 text-center">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-neutral-500 py-12 text-center">
          {tab === 'open' ? 'No open offers right now.' : 'You have no marketplace requests yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => {
            const isMine = r.offeredByEmployeeId === myId;
            const isSwapCounterparty =
              r.requestType === 'Swap' && r.requestedShift?.employeeId === myId;
            const canAccept =
              r.status === 'Open' &&
              !isMine &&
              (r.requestType === 'Giveaway' || isSwapCounterparty);
            const canDecline = r.status === 'Open' && isSwapCounterparty;
            const canCancel =
              isMine && (r.status === 'Open' || r.status === 'PendingApproval' || r.status === 'Accepted');

            return (
              <div
                key={r.id}
                className="bg-white rounded-lg border border-neutral-200 shadow-sm p-4 space-y-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-neutral-900">
                      {r.requestType === 'Swap' ? 'Swap proposal' : 'Cover / extra shift'}
                    </div>
                    <div className="text-sm text-neutral-600">
                      Offered by {r.offeredByName || `Employee #${r.offeredByEmployeeId}`}
                      {r.locationName ? ` · ${r.locationName}` : ''}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusClass(r.status)}`}>
                    {r.status}
                  </span>
                </div>

                <div className="text-sm text-neutral-700 space-y-1">
                  <div>
                    <span className="text-neutral-500">Offered: </span>
                    {formatShiftRange(r.offeredShift?.startTime, r.offeredShift?.endTime)}
                    {r.offeredShift?.positionTitle ? ` · ${r.offeredShift.positionTitle}` : ''}
                  </div>
                  {r.requestedShift && (
                    <div>
                      <span className="text-neutral-500">In exchange: </span>
                      {formatShiftRange(r.requestedShift.startTime, r.requestedShift.endTime)}
                      {r.requestedShift.employeeName ? ` · ${r.requestedShift.employeeName}` : ''}
                    </div>
                  )}
                  {r.notes && <div className="italic text-neutral-500">{r.notes}</div>}
                  {r.rejectionReason && (
                    <div className="text-red-700">Reason: {r.rejectionReason}</div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {canAccept && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => runAction(r.id, () => shiftMarketplaceApi.accept(r.id))}
                      className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Accept
                    </button>
                  )}
                  {canDecline && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => runAction(r.id, () => shiftMarketplaceApi.decline(r.id))}
                      className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Decline
                    </button>
                  )}
                  {canCancel && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => {
                        if (window.confirm('Cancel this marketplace request?')) {
                          runAction(r.id, () => shiftMarketplaceApi.cancel(r.id));
                        }
                      }}
                      className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShiftMarketplace;
